import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";
import { Plus, Search, Play, ExternalLink, Pencil } from "lucide-react";
import EditAppModal from "./EditAppModal";

const CATEGORY_LABEL = {
  compliance: "Compliance",
  system_health: "System Health",
  internal: "Internal",
};

function AppCard({ app, onEdit }) {
  const Icon = Icons[app.icon] || Icons.LayoutGrid;
  const badge = app.status_badge || app.badge;
  return (
    <div className="relative rounded-xl border border-border bg-card p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors group">
      <div className="flex items-start justify-between">
        {app.icon_image ? (
          <img src={app.icon_image} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-11 h-11 rounded-lg object-cover border border-border" />
        ) : (
          <div className="w-11 h-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
        )}
        {badge && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wide bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
            {badge}
          </span>
        )}
      </div>
      <div>
        <h3 className="text-[15px] font-semibold">{app.name}</h3>
        <p className="text-[12.5px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">{app.description}</p>
      </div>
      <div className="flex flex-wrap gap-1">
        <span className="text-[10.5px] rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-muted-foreground">
          {CATEGORY_LABEL[app.category] || app.category}
        </span>
      </div>
      <div className="mt-auto flex items-center gap-2">
        <a href={app.url || "#"} target={app.url?.startsWith("/") ? "_self" : "_blank"} rel="noopener noreferrer"
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-primary text-primary-foreground text-[13px] font-semibold hover:bg-primary/90">
          <Play className="w-4 h-4" /> Launch
        </a>
        {app.url && !app.url.startsWith("/") && app.url !== "#" && (
          <a href={app.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-border text-muted-foreground hover:text-primary hover:border-primary/40">
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
      <button onClick={() => onEdit(app)}
        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 rounded-full bg-primary text-primary-foreground shadow-lg inline-flex items-center justify-center hover:bg-primary/90"
        title="Edit app">
        <Pencil className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function AppsManager() {
  const { toast } = useToast();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/apps");
      setApps(data || []);
    } catch {
      toast({ title: "Failed to load apps", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const filtered = apps.filter((a) =>
    !query.trim() ? true : (a.name + " " + (a.description || "")).toLowerCase().includes(query.toLowerCase())
  );

  const onDelete = async (app) => {
    try {
      await api.delete(`/admin/apps/${app.id}`);
      toast({ title: `Deleted ${app.name}` });
      setEditing(null); load();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-[15px] font-semibold">Apps</h3>
          <p className="text-[12.5px] text-muted-foreground">Register, edit and remove application tiles. {apps.length} total.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search apps…" className="pl-9 h-10 w-64" />
          </div>
          <Button onClick={() => setEditing({})} className="h-10 gap-1.5">
            <Plus className="w-4 h-4" /> Register app
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-48 rounded-xl bg-muted/40 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a) => <AppCard key={a.id} app={a} onEdit={setEditing} />)}
          {!filtered.length && (
            <div className="col-span-full rounded-xl border border-dashed border-border py-14 text-center text-muted-foreground">No apps match “{query}”.</div>
          )}
        </div>
      )}

      {editing && (
        <EditAppModal
          app={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDelete={editing.id && !editing.built_in ? () => onDelete(editing) : null}
        />
      )}
    </div>
  );
}
